<template>
	<div
		class="time-picker-holder prevent-highlight-mobile"
		v-click-outside="hideByClick"
	>
		<span
			v-if="!isStatic"
			class="time-picker-opener"
			:class="{
				highlighted: hasError,
				'time-picker-opener--field': openerType === openerButtonType[0],
				'time-picker-opener--button': openerType === openerButtonType[1]
			}"
			@click="showTimePicker"
		>
			<span
				v-if="openerType === openerButtonType[0]"
				v-html="selectedTime"
				class="time-data"
			/>
			<template v-else>
				<span class="time-picker-opener__title">Pickup</span>
				<span class="time-picker-opener__text">
					<span
						class="time-picker-opener__time"
						:class="{
							'time-picker-opener__time--centered-grey':
								isListEmpty && !selectedTime
						}"
					>
						<span v-if="selectedTime" v-html="selectedTime" class="time-data" />
						<template v-else>
							<template v-if="!isListEmpty">
								Schedule <span class="hidden-mobile">your order</span>
							</template>
							<template v-else>
								Unavailable
							</template>
						</template>
					</span>
					<img
						v-if="!isListEmpty"
						class="time-picker-opener__arrows"
						:src="iconArrow"
						alt="arrow"
					/>
				</span>
			</template>
		</span>
		<div
			v-if="(isPickerVisible || isStatic) && timeList.length"
			class="time-picker"
			ref="timePicker"
			:class="{ 'time-picker--dark': darkTheme }"
		>
			<div class="time-picker-title-wrap">
				<close-btn
					class="time-picker-close"
					:color="darkTheme ? '#fff' : ''"
					@close="hideTimePicker"
				/>
				<span
					v-if="isPickUpTimeValid || (!pickUpTimeFrom && !pickUpTimeTo)"
					class="time-picker-title"
				>
					Pickup Time
				</span>
				<template v-else>
					<span class="time-picker-title">Inappropriate Time</span>
					<p>
						Selected pickup time is no longer available. Would you like to
						re-schedule the order?
					</p>
				</template>
			</div>
			<div class="time-picker-date-wrap">
				<swiper
					class="time-picker-time"
					ref="time-picker-swiper-time"
					:options="swiperOption"
					:scrollbar="{ draggable: true }"
				>
					<swiper-slide v-for="(item, i) in timeList" :key="i">
						<template
							v-if="
								i === 0 &&
									isPickupPeriodAvailableNow &&
									!isWebVersionAllowASAPOption
							"
						>
							ASAP
						</template>
						<template v-else>
							<template v-if="item.startTime === 'ASAP'">
								{{ item.startTime }}
							</template>
							<template v-else>
								{{ callGetAmPmTimeFromDate(item.startTime, AMPMFormat) }} -
								{{ callGetAmPmTimeFromDate(item.endTime, AMPMFormat) }}
							</template>
						</template>
					</swiper-slide>
				</swiper>
			</div>
			<div class="time-picker-buttons-holder">
				<button
					class="time-picker-success button"
					:class="{
						disabled: !this.timeList.length || loading
					}"
					:disabled="!this.timeList.length || loading"
					type="button"
					@click="changeTime"
				>
					Update
				</button>
			</div>
		</div>
		<portal to="modal">
			<transition name="fade">
				<unavailability-modal
					v-if="isUnAvailableModalOpen"
					v-bind="unAvailableModalProps"
					@on-action="onModalAction"
					@close="closeUnAvailableModal"
				/>
			</transition>
		</portal>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { Swiper, SwiperSlide } from 'vue-awesome-swiper'
import 'swiper/css/swiper.css'
import 'swiper/components/scrollbar/scrollbar.scss'

import { clickOutside } from '@/utils/helpers'

import {
	getAmPmTimeFromDate,
	getFormattedDate,
	getFormattedTime,
	mergeFormattedDateTime,
	getCleanHTML
} from '@/utils/helpers'

import CloseBtn from '@/components/CloseBtn.vue'
import UnavailabilityModal from '@/components/modals/UnavailabilityModal.vue'
import IconArrow from '@/assets/images/ic_arrow-up-down.svg'

import {
	ALL_ORDER_TYPES_NAMES,
	DEFAULT_UNAVAILABLE_MODAL_PROPS,
	TIME_FORMATS
} from '@/constants'

const OPENER_TYPE = ['filed', 'button']

export default {
	name: 'PickUpTimePicker',

	directives: {
		'click-outside': clickOutside
	},

	components: {
		UnavailabilityModal,
		CloseBtn,
		Swiper,
		SwiperSlide
	},

	props: {
		pickUpTime: {
			type: Object,
			default: () => ({})
		},
		pickUpTimeFrom: {
			type: String,
			default: ''
		},
		pickUpTimeTo: {
			type: String,
			default: ''
		},
		isPickUpTimeValid: {
			type: Boolean,
			default: true
		},
		showPickUpTimePicker: {
			type: Number,
			default: 0
		},
		cancelText: {
			type: String
		},
		isStatic: {
			type: Boolean,
			default: false
		},
		hasError: {
			type: Boolean,
			default: false
		},
		darkTheme: {
			type: Boolean,
			default: true
		},
		openerType: {
			type: String,
			default: OPENER_TYPE[0],
			validator: value => {
				return OPENER_TYPE.includes(value)
			}
		},
		checkOnOpen: {
			type: Boolean,
			default: false
		},
		needScrollAfterOpening: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			swiperOption: {
				direction: 'vertical',
				slidesPerView: 3,
				centeredSlides: true,
				mousewheel: true,
				slideToClickedSlide: true
			},
			timeSwiper: null,
			dateSwiper: null,
			isPickerVisible: false,
			loading: false,
			unAvailableModalProps: { ...DEFAULT_UNAVAILABLE_MODAL_PROPS },
			isUnAvailableModalOpen: false
		}
	},

	mounted() {
		this.$nextTick(() => {
			this.initDateTime()
		})
	},

	computed: {
		...mapState('venue', [
			'venueInfo',
			'currentPickupAvailablePeriods',
			'pickUpHours'
		]),
		...mapState('cart', ['isCartRecreated', 'orderType', 'isAsap']),
		...mapState('ui', ['isMobileScreen']),
		...mapGetters('venue', ['isWebVersionAllowASAPOption']),

		AMPMFormat() {
			return TIME_FORMATS.AMPM
		},

		timeZone() {
			return this.venueInfo.timeZone
		},

		openerButtonType() {
			return OPENER_TYPE
		},

		isPickupPeriodAvailableNow() {
			return this.currentPickupAvailablePeriods.length
		},

		isNeedAddASAPOption() {
			return this.isPickupPeriodAvailableNow && this.isWebVersionAllowASAPOption
		},

		datesList() {
			return Object.keys(this.pickUpTime) || []
		},

		isListEmpty() {
			return !this.timeList.length
		},

		timeList() {
			const list = []

			this.datesList.forEach(date => {
				const dayList = this.pickUpTime[date].map(item => {
					const startTime = mergeFormattedDateTime(
						date,
						item.startTime,
						this.timeZone
					)
					const endTime = mergeFormattedDateTime(
						date,
						item.endTime,
						this.timeZone
					)

					return { startTime, endTime }
				})
				list.push(...dayList)
			})

			if (this.isNeedAddASAPOption && list.length) {
				list.unshift({ startTime: 'ASAP', endTime: 'ASAP' })
			}

			return list
		},

		selectedTime() {
			if (
				this.pickUpTimeFrom &&
				this.pickUpTimeTo &&
				this.pickUpTimeFrom !== 'ASAP'
			) {
				const firstDateItem = this.timeList[0]
				const formattedFrom = this.callGetAmPmTimeFromDate(this.pickUpTimeFrom)
				const formattedTo = this.callGetAmPmTimeFromDate(this.pickUpTimeTo)

				if (
					formattedFrom ===
						this.callGetAmPmTimeFromDate(firstDateItem.startTime) &&
					formattedTo === this.callGetAmPmTimeFromDate(firstDateItem.endTime) &&
					this.isPickupPeriodAvailableNow &&
					!this.isWebVersionAllowASAPOption
				) {
					return 'ASAP'
				}

				return getCleanHTML(
					`${this.callGetAmPmTimeFromDate(
						this.pickUpTimeFrom,
						this.AMPMFormat
					)} - ${this.callGetAmPmTimeFromDate(
						this.pickUpTimeTo,
						this.AMPMFormat
					)}`
				)
			}

			return this.isAsap ? 'ASAP' : ''
		},

		iconArrow() {
			return IconArrow
		}
	},

	watch: {
		isPickerVisible(val) {
			if (val) {
				this.$nextTick(() => {
					this.initDateTime()
				})
			}
		},
		showPickUpTimePicker() {
			this.showTimePicker()
		}
	},

	methods: {
		...mapActions('cart', ['getCart']),

		initSwipers() {
			const timeSwiperObj = this.$refs['time-picker-swiper-time']

			this.timeSwiper = timeSwiperObj && timeSwiperObj.$swiper
		},

		initDateTime() {
			this.initSwipers()
			let selectedTimeIndex = 0

			if (this.pickUpTimeFrom && this.pickUpTimeTo) {
				const selectedDate = getFormattedDate(
					this.pickUpTimeFrom,
					this.timeZone
				)
				const selectedTime = getFormattedTime(
					this.pickUpTimeFrom,
					this.timeZone
				)

				selectedTimeIndex = this.timeList.findIndex(timeItem => {
					const { startTime } = timeItem

					return (
						startTime.includes(selectedDate) && startTime.includes(selectedTime)
					)
				})
				selectedTimeIndex = selectedTimeIndex >= 0 ? selectedTimeIndex : 0
			}

			this.timeSwiper && this.timeSwiper.slideTo(selectedTimeIndex, 200, false)
		},

		hideByClick(e) {
			const { currentTarget } = e
			let { target } = e

			while (target && target !== currentTarget) {
				const isOpener = target.classList.contains('time-picker-opener')

				if (isOpener) {
					return false
				} else {
					if (target.classList.contains('time-picker')) {
						this.hideTimePicker()
					}
					target = target.parentNode
				}
			}

			this.hideTimePicker()
		},

		onModalAction(action) {
			this.isUnAvailableModalOpen = false
			action()
		},

		closeUnAvailableModal() {
			this.isUnAvailableModalOpen = false
		},

		showTimePicker() {
			if (this.checkOnOpen && this.isListEmpty) {
				const isEmptyList = true
				this.$emit('picker-opened', isEmptyList)

				this.unAvailableModalProps = {
					...DEFAULT_UNAVAILABLE_MODAL_PROPS,
					actionText: '',
					action: () => {},
					orderType: ALL_ORDER_TYPES_NAMES.pickUp,
					availablePeriod:
						(this.pickUpHours &&
							this.pickUpHours.length &&
							this.pickUpHours[0]) ||
						{}
				}

				this.isUnAvailableModalOpen = true

				return
			}

			this.isPickerVisible = true

			if (this.needScrollAfterOpening) {
				this.$nextTick(() => {
					this.$refs?.timePicker?.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
						inline: 'nearest'
					})
				})
			}

			this.$emit('picker-opened')
		},

		hideTimePicker() {
			this.isPickerVisible = false
			this.$emit('close')
		},

		cancel() {
			this.$emit('cancel')
			this.hideTimePicker()
		},

		callGetAmPmTimeFromDate(dateTime, format = null) {
			return format
				? getAmPmTimeFromDate(dateTime, this.timeZone, format)
				: getAmPmTimeFromDate(dateTime, this.timeZone)
		},

		async changeTime() {
			this.loading = true
			const { activeIndex } = this.timeSwiper

			await this.getCart(true)

			if (this.isCartRecreated) {
				this.loading = false

				return
			}

			this.loading = false
			const time = this.timeList[activeIndex]

			this.$emit('change-time', {
				pickUpTimeFrom: time.startTime,
				pickUpTimeTo: time.endTime
			})

			this.hideTimePicker()
		}
	}
}
</script>

<style lang="scss">
@import './src/assets/styles/variables';

.time-picker-holder {
	min-width: 180px;
	position: relative;
}

.time-picker-opener {
	display: block;

	&--field {
		min-height: 30px;
		padding: 6px 35px 6px 1px;
		color: #fff;
		border-bottom: 1px solid #9b9b9b;
		position: relative;
		cursor: pointer;

		&:before {
			position: absolute;
			left: 0;
			right: 0;
			bottom: -1px;
			height: 1px;
			overflow: hidden;
			transform: scale(0.1);
			animation: input-pulse-red 1.1s 3;
			content: '';
			display: none;
		}
		.time-data br {
			display: none;
		}
		&.highlighted {
			border-color: $alertRed;

			&:before {
				display: block;
			}

			&:after {
				border-color: $alertRed;
			}
		}

		&:after {
			position: absolute;
			right: 14px;
			top: 10px;
			width: 7px;
			height: 7px;
			border: solid #fff;
			border-width: 0 1px 1px 0;
			transform: rotate(45deg);
			content: '';
		}
	}

	&--button {
		display: flex;
		align-items: center;
		min-height: 46px;
		min-width: 150px;
		padding: 4px 10px;
		color: $white;
		border: 1px solid $border-color;
		position: relative;
		cursor: pointer;
		border-radius: 24px;
		transition: all 0.2s;

		.time-data br {
			@media (min-width: 470px) {
				display: none;
			}
		}

		@media (min-width: 480px) {
			min-width: 200px;
			padding: 4px 16px;
		}

		@media (min-width: 640px) {
			padding: 12px 16px;
			min-width: 250px;
			font-size: 16px;
		}

		&:hover {
			background-color: rgba($light-blue, 0.25);
			border-color: transparent;
		}

		&.highlighted {
			border-color: $alertRed;
		}

		.time-picker-opener__title {
			display: inline-block;
			position: relative;
			color: $light-blue;
			padding-right: 8px;
			margin-right: 8px;

			@media (min-width: 640px) {
				padding-right: 17px;
				margin-right: 16px;
			}

			&::after {
				content: '';
				position: absolute;
				top: 0;
				bottom: 0;
				right: 0;
				width: 1px;
				background-color: $white;
			}
		}

		.time-picker-opener__text {
			display: inline-flex;
			align-items: center;
			flex-grow: 1;

			.time-picker-opener__time {
				font-size: 12px;
				flex-grow: 1;
				white-space: nowrap;

				&--centered-grey {
					text-align: center;
					color: $grey;
				}

				@media (min-width: 640px) {
					font-size: 14px;
				}
				@media (min-width: 1024px) {
					font-size: 16px;
				}
			}

			.time-picker-opener__arrows {
				margin-left: 2px;
				display: inline-block;

				@media (min-width: 640px) {
					margin-left: 6px;
				}
			}
		}
	}
}

.time-picker {
	position: absolute;
	bottom: calc(100% + 35px);
	left: 0;
	padding: 16px 24px;
	width: calc(100vw - 16px);
	background: $white;
	border-radius: 16px;
	box-shadow: 0 0 16px 3px rgba(0, 0, 0, 0.3);
	z-index: 1000;

	@media (min-width: 480px) {
		width: 360px;
	}

	@media (min-width: 768px) {
		width: 420px;
	}

	.swiper-container {
		height: 160px;
	}

	.swiper-slide {
		height: auto;
		font-size: 14px;
		color: #adadad;
		position: relative;

		&:before {
			position: absolute;
			left: 0;
			top: -1px;
			right: 0;
			border-top: 1px solid $border-color;
			content: '';
		}

		&:last-child {
			&::after {
				position: absolute;
				left: 0;
				bottom: -1px;
				right: 0;
				border-bottom: 1px solid $border-color;
				content: '';
			}
		}
	}

	.swiper-slide-active {
		font-size: 16px;
		color: #333;
	}

	&--dark {
		background: $background-popup;
		border: 1px solid $border-color;

		.time-picker-title-wrap,
		.time-picker-date-wrap {
			border-color: $border-color;
		}

		.swiper-slide {
			color: $grey;

			&:before {
				border-color: $border-color;
			}
		}

		.time-picker-title,
		.swiper-slide-active {
			color: $white;
		}
	}
}

.time-picker-time {
	line-height: 54px;
}

.time-picker-title-wrap {
	position: relative;
	padding: 0 16px 2px 0;
	border-bottom: 1px solid #cdcdcd;
	font-weight: 800;

	.time-picker-close {
		top: 7px;
		right: 1px;
		left: auto;
		background-color: transparent;
	}

	p {
		margin: 0 0 16px;
	}
}

.time-picker-title {
	display: block;
	font-size: 20px;
	color: #816cc2;
	padding: 0 0 12px;
}

.time-picker-date-wrap {
	margin: 0 0 24px;
	padding: 0;
	font-size: 20px;
	color: #333;
	text-align: center;
	overflow: hidden;
	border-bottom: 1px solid #cdcdcd;

	.swiper-container {
		transform: translateY(1px);
	}
}

.time-picker-date-title {
	display: block;
	padding: 0 0 1px;
	font-size: 14px;
	color: #9b9b9b;
}

.time-picker-buttons-holder {
	padding: 0;
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	flex-basis: 50%;

	.time-picker-success {
		width: 100%;
		height: 48px;
		min-height: 48px;
		padding: 15px 36px;
		font-size: 16px;
		border-radius: 32px;
	}
}

@media (max-width: 767px) {
	.time-picker-date-holder {
		border: 0;
		margin: 0;
	}

	.time-picker-buttons-holder {
		overflow: hidden;
		padding: 0;
	}
}
</style>
